import React, {useRef, useState} from 'react';
import {filterOptionList, optionSearchKeyAction} from './filter-option-list';
import './option-filter-list.scss';

const SEARCH_PLACEHOLDER = 'Search';

type OptionFilterListViewProps = {
    inputId: string;
    listId: string;
    clearLabel: string;
    allLabel: string;
    emptyMessage: string;
    checkboxClassName: string;
    options: readonly string[];
    selected: readonly string[];
    query: string;
    onQueryChange: (query: string) => void;
    onToggle: (checked: boolean, value: string) => void;
};

export const OptionFilterListView: React.FC<OptionFilterListViewProps> = ({inputId, listId, clearLabel, allLabel, emptyMessage, checkboxClassName, options, selected, query, onQueryChange, onToggle}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const filtered = filterOptionList(options, query);
    const emptyMessageId = `${inputId}-empty`;

    const clearQuery = () => {
        inputRef.current?.focus();
        onQueryChange('');
    };

    const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const action = optionSearchKeyAction(event.key, query);

        if (action === 'none') {
            return;
        }

        event.preventDefault();

        if (action === 'clear') {
            // A non-empty query consumes Escape so the mobile drawer stays open.
            event.stopPropagation();
            clearQuery();
        }
    };

    return (
        <>
            <div className="option-list-search">
                <div className="option-list-search-field">
                    <input
                        ref={inputRef}
                        id={inputId}
                        className="option-list-search-input"
                        type="text"
                        value={query}
                        placeholder={SEARCH_PLACEHOLDER}
                        aria-label={SEARCH_PLACEHOLDER}
                        autoComplete="off"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        aria-controls={listId}
                        aria-describedby={filtered.showNoMatches ? emptyMessageId : undefined}
                        onChange={event => onQueryChange(event.target.value)}
                        onKeyDown={onSearchKeyDown}
                    />
                    {query !== '' && (
                        <button
                            type="button"
                            className="option-list-search-clear"
                            aria-label={clearLabel}
                            onMouseDown={event => event.preventDefault()}
                            onClick={clearQuery}
                        >
                            <span aria-hidden="true">×</span>
                        </button>
                    )}
                </div>
                {filtered.showNoMatches && (
                    <p
                        id={emptyMessageId}
                        className="option-list-search-empty"
                        role="status"
                    >
                        {emptyMessage}
                    </p>
                )}
            </div>
            <div
                className="filters-scrollable"
                id={listId}
            >
                {filtered.visible.map(option => (
                    <label key={option === '' ? 'all-option' : `named:${option}`}>
                        <input
                            className={checkboxClassName}
                            type="checkbox"
                            value={option}
                            checked={selected.includes(option)}
                            onChange={event => onToggle(event.target.checked, option)}
                        />
                        {option || allLabel}
                    </label>
                ))}
            </div>
        </>
    );
};

type OptionFilterListProps = Omit<OptionFilterListViewProps, 'query' | 'onQueryChange'>;

export const OptionFilterList: React.FC<OptionFilterListProps> = props => {
    const [query, setQuery] = useState('');

    return (
        <OptionFilterListView
            {...props}
            query={query}
            onQueryChange={setQuery}
        />
    );
};
